import $ from "jquery";
import deferred from "flow/promise/deferred";
import config from "flow/config";
import environment from "flow/configuration/environment";
import MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";
import { requiresHeartbeat, timeoutOccurred } from "./httpStatusInterpret";
import { assertSession } from "./assertSession";
import heartbeat from "flow/heartbeat";
import ajaxErrorHelper from "./ajaxErrorHelper";

interface ICallContext {
    baseUrl: string;
    url: string;
    absoluteUrl: string;
    options: Dictionary;
    dfd: JQueryDeferred<any>;
    overrideDefaultErrorHandling: boolean;
    abortController?: AbortController;
}

function appendBaseUrlIfNeeded(baseUrl: string, url: string) {
    const httpPattern = /^https?:\/\//i;
    const rootPattern = /^\/.*/;
    return httpPattern.test(url) || rootPattern.test(url)
        ? url
        : endsWith(baseUrl, "/")
          ? baseUrl + url
          : baseUrl + "/" + url;
}

function endsWith(text: string, ending: string) {
    if (!text || !ending) {
        return false;
    }

    if (ending.length > text.length) {
        return false;
    }

    return text.substring(text.length - ending.length, text.length) === ending;
}

function isDiscoveryCall(url: string): boolean {
    return (
        url.startsWith(config.partialUrls.getProductConfigManifest) ||
        url.startsWith(config.partialUrls.manifest) ||
        url.startsWith(config.partialUrls.validateAndSanitize)
    );
}

function internalAjax<T>(
    myFlow: MyFlow,
    baseUrl: string,
    url: string,
    options: Dictionary,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const dfd = deferred<T>(myFlow);

    const systemDefaultTimeout = isDiscoveryCall(url)
        ? (environment.get(environment.keys.discoveryAjaxTimeout) as number)
        : (environment.get(environment.keys.genericAjaxTimeout) as number);

    options.timeout = overrideDefaultTimeout
        ? Math.max(overrideDefaultTimeout, systemDefaultTimeout)
        : systemDefaultTimeout;

    const absoluteUrl = appendBaseUrlIfNeeded(baseUrl, url);

    const callContext: ICallContext = {
        baseUrl,
        url,
        absoluteUrl,
        options,
        dfd,
        overrideDefaultErrorHandling,
        abortController
    };

    const jqXHR = $.ajax(absoluteUrl, options);

    jqXHR
        .catch(handlePossibleRedirect.bind(null, myFlow, callContext))
        .then(function (...params: any[]) {
            dfd.resolve(...params);
        })
        .catch(handleErrors.bind(null, myFlow, callContext));

    attachAbortSignal(jqXHR, abortController);

    return dfd.promise();
}

function handlePossibleRedirect(
    myFlow: MyFlow,
    callContext: ICallContext,
    jqXHR: JQuery.jqXHR,
    textStatus: string,
    errorThrown: any
) {
    if (requiresHeartbeat(jqXHR.status)) {
        // Attempt renew session
        const dfd = $.Deferred();
        // It might be the first time we connect this web project
        // So we begin with a regular heartbeat
        heartbeat
            .tick(callContext.baseUrl, environment.get<number>(environment.keys.heartbeatTimeout))
            // The ajax version of the heartbeat will be our real way to determine if we still have a session.
            // So even if the script version failed, we will try the ajax version.
            .catch(() => Promise.resolve())
            // We don't know how exactly it went, so we will try to determine if there still are problems and resolve them.
            .then(() => assertSession(myFlow, callContext.baseUrl))
            // Retry the original request
            .then(() => {
                const jqXHR = $.ajax(callContext.absoluteUrl, callContext.options);
                attachAbortSignal(jqXHR, callContext.abortController);
                return jqXHR.then(dfd.resolve, dfd.reject);
            })
            // Catch any errors that might have occured in assertSession
            .catch(e => dfd.reject(e));

        // Only JQuery promises can resolve with multiple parameters.
        // Native ones only one parameter (which could of course be an array).
        // But to not break existing behavior we need to make sure we return a JQuery promise.
        return dfd.promise();
    } else {
        // Just forward the original response to error handler
        return $.Deferred().resolve(jqXHR, textStatus, errorThrown);
    }
}

function handleErrors(
    myFlow: MyFlow,
    callContext: ICallContext,
    jqXHR: JQuery.jqXHR,
    textStatus: string,
    errorThrown: any
) {
    if (timeoutOccurred(jqXHR.status, textStatus)) {
        myFlow.error(
            `Timeout: Call to ${callContext.absoluteUrl} took more than ${callContext.options.timeout} milliseconds to respond.`
        );
    } else if (callContext.url && callContext.url.indexOf(config.writeLogUrl) >= 0) {
        // This is a syslog call.
        // We want to avoid logging an error that ultimately generates a new failing syslog call, hence creating a loop.
        console.warn(`Failed syslog call to ${callContext.absoluteUrl}`, jqXHR, textStatus, errorThrown);
    } else if (ajaxErrorHelper.isBusinessException(jqXHR)) {
        // Don't bother syslog error level with controlled failed server validations.
        myFlow.log("Server validation failed:", ajaxErrorHelper.getErrorMessageFromResponse(jqXHR, textStatus));
    } else if (textStatus === "abort") {
        // The call was aborted. We let the caller handle this.
        callContext.dfd.reject(jqXHR, textStatus, errorThrown);
        return;
    } else {
        myFlow.error({
            message: `Call to ${callContext.absoluteUrl} failed.`,
            statusCode: jqXHR.status,
            statusText: textStatus,
            responseDetails: jqXHR.responseJSON
        });

        if (errorThrown) {
            myFlow.logObjectsAsErrors(errorThrown);
        }

        myFlow.logObjectsDebug(jqXHR, textStatus);
    }

    if (myFlow.useGenericAjaxErrorHandler && callContext.overrideDefaultErrorHandling !== true) {
        myFlow.handleAjaxError(jqXHR);
    }

    callContext.dfd.reject(jqXHR, textStatus, errorThrown);
}

function attachAbortSignal(jqXHR: JQuery.jqXHR, abortController?: AbortController) {
    if (abortController instanceof AbortController) {
        const abortFn = () => {
            jqXHR.abort();
        };

        abortController.signal.addEventListener("abort", abortFn);

        jqXHR.always(() => {
            abortController.signal.removeEventListener("abort", abortFn);
        });
    }
}

function ajax<T>(
    myFlow: MyFlow,
    url: string,
    options: Dictionary,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    return internalAjax(
        myFlow,
        myFlow.baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function getJSONWithAnotherBaseUrl<T>(
    myFlow: MyFlow,
    baseUrl: string,
    url: string,
    data: Dictionary,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const options: Dictionary = {
        dataType: "json"
    };

    if (data !== undefined) {
        options.data = data;
    }

    return internalAjax(
        myFlow,
        baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function getJSON<T>(
    myFlow: MyFlow,
    url: string,
    data: Dictionary,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    return getJSONWithAnotherBaseUrl(
        myFlow,
        myFlow.baseUrl,
        url,
        data,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function get<T>(
    myFlow: MyFlow,
    url: string,
    data?: any,
    dataType?: string,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const options: Dictionary = {};

    if (dataType !== undefined) {
        options.data = data;
        options.dataType = dataType;
    } else {
        options.dataType = data;
    }
    return internalAjax(
        myFlow,
        myFlow.baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function postJSONWithAnotherBaseUrl<T>(
    myFlow: MyFlow,
    baseUrl: string,
    url: string,
    data: any,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const options = {
        data: data,
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        type: "POST"
    };
    return internalAjax(
        myFlow,
        baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function postJSON<T>(
    myFlow: MyFlow,
    url: string,
    data: any,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    return postJSONWithAnotherBaseUrl(
        myFlow,
        myFlow.baseUrl,
        url,
        data,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function deleteJSON<T>(
    myFlow: MyFlow,
    url: string,
    data: any,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const options = {
        data: data,
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        type: "DELETE"
    };
    return internalAjax(
        myFlow,
        myFlow.baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function putJSON<T>(
    myFlow: MyFlow,
    url: string,
    data: any,
    overrideDefaultErrorHandling?: boolean,
    overrideDefaultTimeout?: number,
    abortController?: AbortController
): JQueryPromise<T> {
    const options = {
        data: data,
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        type: "PUT"
    };

    return internalAjax(
        myFlow,
        myFlow.baseUrl,
        url,
        options,
        overrideDefaultErrorHandling,
        overrideDefaultTimeout,
        abortController
    );
}

function sendBeacon(myFlow: MyFlow, url: string, data?: BodyInit | null): boolean {
    const absoluteUrl = appendBaseUrlIfNeeded(myFlow.baseUrl, url);

    return navigator.sendBeacon(absoluteUrl, data);
}

export default {
    ajax: ajax,
    getJSONWithAnotherBaseUrl: getJSONWithAnotherBaseUrl,
    postJSONWithAnotherBaseUrl: postJSONWithAnotherBaseUrl,
    getJSON: getJSON,
    get: get,
    postJSON: postJSON,
    deleteJSON: deleteJSON,
    putJSON: putJSON,
    sendBeacon: sendBeacon
};
